import {
    AfterViewInit,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { NotificationsService } from "angular2-notifications";
import { get } from "lodash";
import moment from "moment";
import { GoogleAnalyticsService } from "../../services/analytics/google-analitics-service";
import { SocketService } from "../../services/socket/socket.service";
import { DialogForgotPassword } from "../home/dialog-component/forgot-password/dialog-forgot-password.component";
import { DialogRegistrationComponent } from "../home/dialog-component/registration/registration.component";
import { AuthService } from "./../../services/auth/auth.service";
import { LocalStorageService } from "./../../services/local-storage/local-storage.service";
import { environment } from "environments/environment";

type LoginSeason = "primavera" | "estate" | "autunno" | "inverno";

interface SeasonAnimation {
    distruggi: () => void;
}

interface SeasonAnimationApi {
    mount: (
        target: HTMLElement,
        options: { svg: string; fit: "cover" | "contain" },
    ) => SeasonAnimation | Promise<SeasonAnimation> | null;
}

const SEASON_ANIMATIONS_PATH = "assets/animations/seasons";

const SEASON_BY_MONTH: LoginSeason[] = [
    "inverno",
    "inverno",
    "primavera",
    "primavera",
    "primavera",
    "estate",
    "estate",
    "estate",
    "autunno",
    "autunno",
    "autunno",
    "inverno",
];

const seasonAnimationScripts = new Map<
    LoginSeason,
    Promise<SeasonAnimationApi>
>();

function loadSeasonAnimation(season: LoginSeason): Promise<SeasonAnimationApi> {
    let loading = seasonAnimationScripts.get(season);
    if (!loading) {
        loading = new Promise<SeasonAnimationApi>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `${SEASON_ANIMATIONS_PATH}/${season}.js`;
            script.addEventListener("load", () =>
                resolve((window as any).DotAnimazione),
            );
            script.addEventListener("error", () => {
                seasonAnimationScripts.delete(season);
                script.remove();
                reject(new Error(`Animazione ${season} non caricata`));
            });
            document.body.appendChild(script);
        });
        seasonAnimationScripts.set(season, loading);
    }
    return loading;
}

@Component({
    standalone: false,
    selector: "login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
})
export class FuseLoginComponent implements OnInit, AfterViewInit, OnDestroy {
    public formGroup: UntypedFormGroup;
    public showSingUp: boolean;
    @ViewChild("seasonBackground", { static: true })
    private seasonBackground: ElementRef<HTMLElement>;
    private seasonAnimation: SeasonAnimation | null = null;
    private destroyed = false;

    constructor(
        private toast: NotificationsService,
        private storage: LocalStorageService,
        private authService: AuthService,
        public dialog: MatDialog,
        private router: Router,
        private googleAnalyticsService: GoogleAnalyticsService,
        private socketService: SocketService,
        private ngZone: NgZone,
    ) {
        this.showSingUp = environment.showSingUp;
        if (!!this.storage.getItem("user")) {
            this.router.navigate(["/home"]);
        }
        this.formGroup = new UntypedFormGroup({
            username: new UntypedFormControl("", [Validators.required]),
            password: new UntypedFormControl("", [Validators.required]),
        });
    }

    ngAfterViewInit(): void {
        this.mountSeasonAnimation();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.seasonAnimation?.distruggi();
        this.seasonAnimation = null;
    }

    private mountSeasonAnimation(): void {
        const season = SEASON_BY_MONTH[moment().month()];
        this.ngZone.runOutsideAngular(() =>
            loadSeasonAnimation(season)
                .then((api) => {
                    if (this.destroyed || !api) {
                        return null;
                    }
                    return api.mount(this.seasonBackground.nativeElement, {
                        svg: `${SEASON_ANIMATIONS_PATH}/${season}.svg`,
                        fit: "cover",
                    });
                })
                .then((animation) => {
                    if (!animation) {
                        return;
                    }
                    if (this.destroyed) {
                        animation.distruggi();
                        return;
                    }
                    this.seasonAnimation = animation;
                })
                .catch(() => undefined),
        );
    }

    ngOnInit() {
        this.googleAnalyticsService.pageEmitter("LoginPage");
    }

    onYesClick(): void {
        this.authService
            .login({
                username: this.formGroup.get("username").value,
                password: this.formGroup.get("password").value,
            })
            .subscribe({
                next: (data) => {
                    this.googleAnalyticsService.eventEmitter(
                        "LoginPage",
                        "Login Successfully",
                    );
                    this.socketService.sendMessage("welcome-message", {
                        userToken: this.authService.getToken().token_session,
                    });
                    this.router.navigate(["home"], {
                        state: {
                            requirePrivacy: data.user.require_privacy_accept,
                        },
                    });
                },
                error: (err) => {
                    const errorMessage = get(err, "error.message", "");
                    if (errorMessage === "USER_NOT_FOUND") {
                        this.googleAnalyticsService.eventEmitter(
                            "LoginPage",
                            "Login Fault (user not found)",
                        );
                        this.toast.error("Errore", "Utente non trovato!");
                    } else if (errorMessage === "USER_DISABLED") {
                        this.googleAnalyticsService.eventEmitter(
                            "LoginPage",
                            "Login Fault (user disabled)",
                        );
                        this.toast.error("Errore", "Utente Disabilitato!");
                    } else if (errorMessage === "WRONG_PASSWORD") {
                        this.googleAnalyticsService.eventEmitter(
                            "LoginPage",
                            "Login Fault (wrong password)",
                        );
                        this.toast.error("Errore", "Password errata!");
                    } else {
                        this.googleAnalyticsService.eventEmitter(
                            "LoginPage",
                            "Login Fault (generic)",
                        );
                        this.toast.error("Errore", "Autenticazione Falita.");
                    }
                },
            });
    }

    Registration(): void {
        this.dialog.open(DialogRegistrationComponent);
    }
    Forgot(): void {
        this.dialog.open(DialogForgotPassword);
    }
}
